import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CompareIcon from '@mui/icons-material/Compare';
import CloseIcon from '@mui/icons-material/Close';
import ReactDOM from 'react-dom';

const CompareContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '90vw',
  height: '80vh',
  maxWidth: '1200px',
  margin: 'auto',
  overflow: 'hidden',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const BaseImage = styled('img')({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
});

const SliderContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '80%',
  maxWidth: '400px',
  backgroundColor: 'rgba(138, 43, 226, 0.15)',
  borderRadius: '20px',
  padding: '12px 24px',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  boxShadow: '0 4px 16px rgba(138, 43, 226, 0.2)',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '20px',
  right: '20px',
  backgroundColor: 'rgba(138, 43, 226, 0.15)',
  color: 'white',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.3)',
    transform: 'scale(1.05)',
  },
}));

const ImageCompare = ({ originalImage, generatedImage, onClose }) => {
  const [sliderValue, setSliderValue] = useState(50);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateDimensions();
    
    window.addEventListener('resize', updateDimensions);
    
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    let percentage = (x / width) * 100;
    percentage = Math.max(0, Math.min(100, percentage));
    
    setSliderValue(percentage);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDragging]);

  const portalRoot = document.getElementById('portal-root');
  if (!portalRoot) {
    console.error('Portal kök elementi bulunamadı');
    return null;
  }
  
  return ReactDOM.createPortal(
    <CompareContainer>
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      
      <ImageContainer ref={containerRef}>
        <BaseImage
          src={originalImage}
          alt="Orijinal"
          style={{ opacity: 1 }}
        />
        <BaseImage
          src={generatedImage}
          alt="Oluşturulan"
          style={{
            clipPath: `inset(0 ${100 - sliderValue}% 0 0)`,
            opacity: 1,
          }}
        />
        
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            left: `${sliderValue}%`,
            top: 0,
            bottom: 0,
            width: '4px',
            backgroundColor: 'rgba(138, 43, 226, 0.8)',
            transform: 'translateX(-50%)',
            cursor: 'ew-resize',
            userSelect: 'none',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '40px',
              backgroundColor: '#8A2BE2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 16px rgba(138, 43, 226, 0.5)',
              border: '2px solid rgba(255, 255, 255, 0.9)',
            },
          }}
        >
          <CompareIcon
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              zIndex: 1,
            }}
          />
        </Box>
      </ImageContainer>

      <SliderContainer>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'white',
            fontWeight: 500,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}
        >
          Orijinal
        </Typography>
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          aria-labelledby="compare-slider"
          sx={{
            color: 'white',
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              backgroundColor: '#8A2BE2',
              border: '2px solid white',
              boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 8px rgba(138, 43, 226, 0.3)',
              },
            },
            '& .MuiSlider-track': {
              height: 4,
              backgroundColor: '#8A2BE2',
            },
            '& .MuiSlider-rail': {
              height: 4,
              opacity: 0.3,
              backgroundColor: 'rgba(138, 43, 226, 0.5)',
            },
          }}
        />
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'white',
            fontWeight: 500,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
          }}
        >
          Oluşturulan
        </Typography>
      </SliderContainer>
    </CompareContainer>,
    portalRoot
  );
};

export default ImageCompare;
